// Firebase Firestore Database Service
import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  QueryConstraint,
  Timestamp,
} from "firebase/firestore";
import { db } from "./config";

// Generic interface for documents with ID
export interface FirebaseDocument {
  id: string;
  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

// Add a document to a collection
export const addDocument = async <T extends object>(
  collectionName: string,
  data: T,
): Promise<string> => {
  try {
    const docData = {
      ...data,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };
    const docRef = await addDoc(collection(db, collectionName), docData);
    return docRef.id;
  } catch (error) {
    console.error("Error adding document:", error);
    throw error;
  }
};

// Get a document by ID
export const getDocument = async <T extends FirebaseDocument>(
  collectionName: string,
  id: string,
): Promise<T | null> => {
  try {
    const docRef = doc(db, collectionName, id);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    } else {
      return null;
    }
  } catch (error) {
    console.error("Error getting document:", error);
    throw error;
  }
};

// Get all documents from a collection
export const getDocuments = async <T extends FirebaseDocument>(
  collectionName: string,
  constraints?: QueryConstraint[],
): Promise<T[]> => {
  try {
    const collectionRef = collection(db, collectionName);
    const q = constraints ? query(collectionRef, ...constraints) : collectionRef;
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  } catch (error) {
    console.error("Error getting documents:", error);
    throw error;
  }
};

// Update a document
export const updateDocument = async <T extends object>(
  collectionName: string,
  id: string,
  data: Partial<T>,
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, id);
    const updateData = {
      ...data,
      updatedAt: Timestamp.now(),
    };
    await updateDoc(docRef, updateData);
  } catch (error) {
    console.error("Error updating document:", error);
    throw error;
  }
};

// Delete a document
export const deleteDocument = async (
  collectionName: string,
  id: string,
): Promise<void> => {
  try {
    const docRef = doc(db, collectionName, id);
    await deleteDoc(docRef);
  } catch (error) {
    console.error("Error deleting document:", error);
    throw error;
  }
};

// Listen to real-time changes in a collection
export const subscribeToCollection = <T extends FirebaseDocument>(
  collectionName: string,
  callback: (documents: T[]) => void,
  constraints?: QueryConstraint[],
) => {
  const collectionRef = collection(db, collectionName);
  const q = constraints ? query(collectionRef, ...constraints) : collectionRef;

  return onSnapshot(q, (querySnapshot) => {
    const documents = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
    callback(documents);
  });
};

// Listen to real-time changes in a document
export const subscribeToDocument = <T extends FirebaseDocument>(
  collectionName: string,
  id: string,
  callback: (document: T | null) => void,
) => {
  const docRef = doc(db, collectionName, id);

  return onSnapshot(docRef, (docSnap) => {
    if (docSnap.exists()) {
      const document = { id: docSnap.id, ...docSnap.data() } as T;
      callback(document);
    } else {
      callback(null);
    }
  });
};

// Get documents with time-based filtering
export const getDocumentsWithTimeFilter = async <T extends FirebaseDocument>(
  collectionName: string,
  userId: string,
  timeField: string = "createdAt",
  filterDate?: Date,
  orderByField: string = "createdAt",
  orderDirection: "asc" | "desc" = "desc",
  limitCount: number = 50,
): Promise<T[]> => {
  try {
    const constraints: QueryConstraint[] = [
      where("userId", "==", userId),
      orderBy(orderByField, orderDirection),
      limit(limitCount),
    ];

    // Add date filter if specified
    if (filterDate) {
      constraints.splice(1, 0, where(timeField, ">=", Timestamp.fromDate(filterDate)));
    }

    return await getDocuments<T>(collectionName, constraints);
  } catch (error) {
    console.error("Error getting documents with time filter:", error);
    throw error;
  }
};

// Get recent documents for a user
export const getRecentDocuments = async <T extends FirebaseDocument>(
  collectionName: string,
  userId: string,
  limitCount: number = 10,
): Promise<T[]> => {
  try {
    const constraints = [
      where("userId", "==", userId),
      orderBy("createdAt", "desc"),
      limit(limitCount),
    ];

    return await getDocuments<T>(collectionName, constraints);
  } catch (error) {
    console.error("Error getting recent documents:", error);
    throw error;
  }
};

// Count documents for a user
export const countUserDocuments = async (
  collectionName: string,
  userId: string,
  filterDate?: Date,
): Promise<number> => {
  try {
    const constraints = [where("userId", "==", userId)];

    if (filterDate) {
      constraints.push(where("createdAt", ">=", Timestamp.fromDate(filterDate)));
    }

    const documents = await getDocuments(collectionName, constraints);
    return documents.length;
  } catch (error) {
    console.error("Error counting user documents:", error);
    throw error;
  }
};

// Export Firestore query helpers for use in consuming code
export { where, orderBy, limit };
